'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import AuthVideoHero from '@/components/platform/auth/AuthVideoHero';
import Link from 'next/link';
import Script from 'next/script';
import Image from 'next/image';
import { RecaptchaProvider } from '@/components/platform/providers/RecaptchaProvider';
import { useEffect } from 'react';

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const { darkMode } = useDarkMode();
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  // Mark page as auth page (for cookie consent visibility)
  useEffect(() => {
    document.documentElement.setAttribute('data-auth-page', 'true');
    return () => {
      document.documentElement.removeAttribute('data-auth-page');
    };
  }, []);

  // NOTE: Dark-mode anti-flash is handled by the "dark-mode-init" script in
  // the root layout (app/layout.tsx), which runs beforeInteractive on ALL
  // pages including /auth. A duplicate script here was removed — it was
  // invalid in a client layout (beforeInteractive is root-layout-only in
  // Next.js) and never executed.
  return (
    <RecaptchaProvider reCaptchaKey={recaptchaSiteKey} theme={darkMode ? 'dark' : 'light'}>
      {/* CookieYes Consent Banner - Only load on production (valiyou.com) */}
      {process.env.NEXT_PUBLIC_SITE_URL === 'https://valiyou.com' && (
        <Script
          id="cookieyes"
          src="https://cdn-cookieyes.com/client_data/3490247b01411d8a5532fef82a9e8b1f/script.js"
          strategy="afterInteractive"
        />
      )}

      <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900" suppressHydrationWarning>
        {/* Left side - Video Background (persistent across auth pages) */}
        <AuthVideoHero />

        {/* Right side - Content (changes per page) */}
        <div className="relative flex flex-1 flex-col justify-center px-4 py-6 sm:px-6 lg:flex-none lg:px-20 lg:py-12 xl:px-24">
          {/* Mobile Logo - Fixed at top, aligned with content column, only visible on small screens */}
          <div className="absolute top-6 right-4 left-4 lg:hidden">
            <div className="mx-auto w-full max-w-sm" suppressHydrationWarning>
              {/* Black logo for light mode */}
              <Image
                src="/Valiyou_black_TM.svg"
                alt="Valiyou"
                width={150}
                height={28}
                priority
                className="h-auto w-[150px] dark:hidden"
                suppressHydrationWarning
              />
              {/* White logo for dark mode */}
              <Image
                src="/Valiyou_white_TM.svg"
                alt="Valiyou"
                width={150}
                height={28}
                priority
                className="hidden h-auto w-[150px] dark:block"
                suppressHydrationWarning
              />
            </div>
          </div>

          <main>{children}</main>

          {/* Footer Links */}
          <div className="absolute right-0 bottom-5 left-0 text-center">
            <div className="flex flex-col items-center justify-center gap-1 text-[10px]">
              <span className="text-gray-600 dark:text-gray-500">
                Get started with{' '}
                <Link
                  href="https://docs.valiyou.com/getting-started/quick-start"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 underline transition-colors hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  Quick Start Guide
                </Link>
                .
              </span>
              <span className="text-gray-600 dark:text-gray-500">
                Your trust is our top priority, visit{' '}
                <Link
                  href="/trust-center"
                  className="text-gray-600 underline transition-colors hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  Valiyou Trust Center
                </Link>{' '}
                here.
              </span>
            </div>

            {/* reCAPTCHA Notice */}
            <p className="mt-2 text-[8px] text-gray-400 dark:text-gray-600">
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-500"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-500"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </div>
      </div>
    </RecaptchaProvider>
  );
}
